// src/components/BulkDeleteModal.jsx
import React, { useMemo, useState, useEffect } from "react";

/**
 * BulkDeleteModal
 * - Paste notepad text
 * - Preview matches
 * - Delete matches (backup enabled)
 *
 * Backend required:
 *   POST  /bulk-delete/by-text
 * Payload:
 *   { text, source: "contacts"|"table", dry_run: true|false, backup: true }
 */

export default function BulkDeleteModal({ open, onClose, baseUrl, token }) {
  const authHeaders = useMemo(() => {
    const h = { "Content-Type": "application/json" };
    if (token) h["Authorization"] = `Bearer ${token}`;
    return h;
  }, [token]);

  const BASE = (baseUrl || "").replace(/\/+$/, "");

  const [text, setText] = useState("");
  const [source, setSource] = useState("contacts"); // contacts | table
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    // Reset each time it opens (safer)
    setErr("");
    setPreview(null);
    setResult(null);
    // keep text if you prefer; I keep it.
  }, [open]);

  if (!open) return null;

  const parseResponse = async (res) => {
    const ct = (res.headers.get("content-type") || "").toLowerCase();
    const raw = await res.text();
    if (ct.includes("application/json")) {
      try {
        return JSON.parse(raw);
      } catch {
        return { message: raw };
      }
    }
    try {
      return JSON.parse(raw);
    } catch {
      return { message: raw };
    }
  };

  const callApi = async (dry_run) => {
    setErr("");
    setResult(null);
    if (dry_run) setPreview(null);

    const payload = {
      text,
      source,
      dry_run: !!dry_run,
      backup: true,
    };

    if (!text.trim()) {
      setErr("Please paste the notepad text first.");
      return;
    }

    setBusy(true);
    try {
      const res = await fetch(`${BASE}/bulk-delete/by-text`, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: authHeaders,
        body: JSON.stringify(payload),
      });

      const data = await parseResponse(res);
      if (!res.ok) {
        throw new Error(data?.error || data?.detail || data?.message || `HTTP ${res.status}`);
      }

      if (dry_run) setPreview(data);
      else setResult(data);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const matched = Array.isArray(preview?.matched) ? preview.matched : [];

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 12,
      }}
      onMouseDown={(e) => {
        // click outside closes
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        style={{
          width: "min(980px, 100%)",
          maxHeight: "92vh",
          overflow: "auto",
          background: "var(--card, #111)",
          border: "1px solid var(--line, rgba(255,255,255,0.12))",
          borderRadius: 16,
          boxShadow: "0 10px 40px rgba(0,0,0,0.45)",
        }}
      >
        {/* Header */}
        <div
          style={{
            position: "sticky",
            top: 0,
            background: "var(--card, #111)",
            borderBottom: "1px solid var(--line, rgba(255,255,255,0.12))",
            padding: "12px 14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            zIndex: 2,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <div style={{ fontSize: 16, fontWeight: 800 }}>🧹 Bulk Delete (Notepad)</div>
            <div style={{ opacity: 0.8, fontSize: 12 }}>
              Paste names + numbers. We detect numbers and delete matching contacts by phone.
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            style={{
              padding: "8px 10px",
              borderRadius: 10,
              border: "1px solid var(--line, rgba(255,255,255,0.12))",
              background: "transparent",
              cursor: busy ? "not-allowed" : "pointer",
              fontWeight: 700,
            }}
          >
            ✕ Close
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 14 }}>
          {err ? (
            <div
              style={{
                padding: 10,
                borderRadius: 12,
                border: "1px solid rgba(255,0,0,0.35)",
                background: "rgba(255,0,0,0.06)",
                marginBottom: 12,
                whiteSpace: "pre-wrap",
              }}
            >
              {err}
            </div>
          ) : null}

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center",
              marginBottom: 10,
            }}
          >
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              Source DB:
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                disabled={busy}
                style={{
                  padding: "8px 10px",
                  borderRadius: 10,
                  border: "1px solid var(--line, rgba(255,255,255,0.12))",
                  background: "transparent",
                }}
              >
                <option value="contacts">contacts.db</option>
                <option value="table">table.db</option>
              </select>
            </label>

            <button
              onClick={() => callApi(true)}
              disabled={busy || !text.trim()}
              style={{
                padding: "9px 12px",
                borderRadius: 12,
                border: "1px solid var(--line, rgba(255,255,255,0.12))",
                background: "transparent",
                cursor: busy ? "not-allowed" : "pointer",
                fontWeight: 800,
              }}
              title="Preview which contacts will be deleted"
            >
              {busy ? "Working..." : "🔎 Preview Matches"}
            </button>

            <button
              onClick={() => callApi(false)}
              disabled={busy || !text.trim()}
              style={{
                padding: "9px 12px",
                borderRadius: 12,
                border: "1px solid rgba(255,80,80,0.35)",
                background: "rgba(255,0,0,0.06)",
                cursor: busy ? "not-allowed" : "pointer",
                fontWeight: 900,
              }}
              title="Deletes matching contacts (backup enabled)"
            >
              {busy ? "Working..." : "🗑 Delete Now"}
            </button>

            <button
              onClick={() => {
                setText("");
                setPreview(null);
                setResult(null);
                setErr("");
              }}
              disabled={busy}
              style={{
                padding: "9px 12px",
                borderRadius: 12,
                border: "1px solid var(--line, rgba(255,255,255,0.12))",
                background: "transparent",
                cursor: busy ? "not-allowed" : "pointer",
                fontWeight: 800,
              }}
            >
              Clear
            </button>
          </div>

          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={busy}
            placeholder={`Paste your notepad here, e.g.
stephanie bassil 3665364
rita abou jaoude 70793528
...
`}
            style={{
              width: "100%",
              minHeight: 220,
              padding: 12,
              borderRadius: 14,
              border: "1px solid var(--line, rgba(255,255,255,0.12))",
              background: "transparent",
              outline: "none",
              resize: "vertical",
              fontSize: 14,
              lineHeight: 1.35,
            }}
          />

          {/* Preview */}
          {preview ? (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontWeight: 900, marginBottom: 6 }}>Preview</div>
              <div style={{ opacity: 0.9, marginBottom: 10 }}>
                Detected numbers: <b>{preview.detected_numbers_count}</b> — Matched contacts:{" "}
                <b>{preview.matched_count}</b>
              </div>

              {matched.length ? (
                <div
                  style={{
                    border: "1px solid var(--line, rgba(255,255,255,0.12))",
                    borderRadius: 14,
                    overflow: "hidden",
                  }}
                >
                  {matched.map((m, idx) => (
                    <div
                      key={`${m.id}-${idx}`}
                      style={{
                        padding: 10,
                        borderTop: idx ? "1px solid rgba(255,255,255,0.08)" : "none",
                      }}
                    >
                      <div style={{ fontWeight: 900 }}>{m.name || "(no name)"}</div>
                      <div style={{ opacity: 0.9 }}>{m.phones}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ opacity: 0.8 }}>No matches.</div>
              )}
            </div>
          ) : null}

          {/* Result */}
          {result ? (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontWeight: 900, marginBottom: 6 }}>Result</div>
              <div style={{ opacity: 0.95 }}>
                Deleted: <b>{result.deleted_count}</b> — Backup:{" "}
                <b>{result.backup_path ? result.backup_path : "not created"}</b>
              </div>
            </div>
          ) : null}

          <div style={{ marginTop: 14, opacity: 0.75, fontSize: 12 }}>
            Matching rule: compares the last 7/8 digits of each pasted number against stored phone tokens,
            so formats like +961, spaces, dashes, parentheses are handled.
          </div>
        </div>
      </div>
    </div>
  );
}
